//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Locked } from '@carbon/icons-react';
import { useUserAuth } from '../../../UserAuth';
import { useTeamspaceContext } from '../../../TeamspaceContext';
import { useConfig } from '../../../Config';
import MCPDataService, {
  UserMCPConnection,
  LOCKED_OOTB_SERVER_NAMES,
} from '../../../utils/MCPDataService';
import { Switch } from '../../ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../../ui/tooltip';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../ui/table';
import { isSafeOAuthUrl } from '../../../utils/validateUrlUtils';
import { safeOAuthNavigate } from '../../../utils/securityUtils';

interface MCPConnectionsTabProps {
  teamspaceId?: string;
  loopId?: string;
}

const MCPConnectionsTab: React.FC<MCPConnectionsTabProps> = ({
  teamspaceId,
  loopId,
}) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useUserAuth();
  const { services } = useConfig();
  const [connections, setConnections] = useState<UserMCPConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const userRef = useRef(user);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const tRef = useRef(t);

  useEffect(() => {
    userRef.current = user;
    isAuthenticatedRef.current = isAuthenticated;
    tRef.current = t;
  }, [user, isAuthenticated, t]);

  const mcpService = useMemo(
    () =>
      new MCPDataService(
        () => userRef.current,
        () => isAuthenticatedRef.current,
        services?.analyticsai?.url,
      ),
    [services?.analyticsai?.url],
  );

  const fetchConnections = useCallback(async () => {
    if (!teamspaceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data: UserMCPConnection[] = await mcpService.userListConnections(
        teamspaceId,
        loopId,
      );
      setConnections(data);
    } catch (err) {
      console.error('Error fetching MCP connections:', err);
      setError(tRef.current('error-fetching-mcp-connections'));
    } finally {
      setIsLoading(false);
    }
  }, [mcpService, teamspaceId, loopId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // Listen for OAuth popup success message to refresh the connection list
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      if (event.data === 'mcp_connected') {
        fetchConnections();
      }
    };
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [fetchConnections]);

  const handleToggle = async (connection: UserMCPConnection) => {
    const isEnabling = !connection.user_enabled;
    if (
      !isEnabling &&
      connection.credential_type === 'internal' &&
      LOCKED_OOTB_SERVER_NAMES.has(connection.name.trim().toLowerCase())
    ) {
      // Defense in depth — only the disable direction is guarded (the
      // Switch is already disabled in that state, see isLocked below); a
      // locked server that's somehow off must stay re-enableable, so the
      // enable direction always falls through.
      return;
    }
    if (!teamspaceId) return;
    const previousConnections = [...connections];

    // Optimistic update: enable this one, disable others in the same domain
    setConnections((prev) =>
      prev.map((c) => {
        if (c.server_id === connection.server_id) {
          return { ...c, user_enabled: isEnabling };
        }
        if (isEnabling && c.domain === connection.domain) {
          return { ...c, user_enabled: false };
        }
        return c;
      }),
    );

    try {
      await mcpService.userToggleConnection(
        teamspaceId,
        connection.server_id,
        isEnabling,
        loopId,
      );
    } catch (err) {
      setConnections(previousConnections);
      setError(t('error-updating-mcp-status'));
    }
  };

  const formatLabel = (text: string) =>
    text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

  const formatCredential = (type: string) => {
    if (type === 'internal') return 'Internal';
    if (type === 'oauth') return 'OAuth';
    return formatLabel(type);
  };

  const handleConnectDisconnect = async (connection: UserMCPConnection) => {
    if (!teamspaceId) return;
    setActionInProgress(connection.server_id);
    const previousConnections = [...connections];
    const isCurrentlyConnected = connection.connected;

    // Optimistic update
    setConnections((prev) =>
      prev.map((c) =>
        c.server_id === connection.server_id
          ? { ...c, connected: !isCurrentlyConnected }
          : c,
      ),
    );

    try {
      if (isCurrentlyConnected) {
        await mcpService.userDisconnect(teamspaceId, connection.server_id);
      } else {
        // Open popup synchronously in the click context to avoid browser blocking
        const popup = window.open(
          'about:blank',
          'mcp_oauth_popup',
          'width=600,height=700',
        );
        const response = await mcpService.userConnect(
          teamspaceId,
          connection.server_id,
        );
        // Handle OAuth redirect if auth_url is returned
        if (
          'auth_url' in response &&
          response.auth_url &&
          isSafeOAuthUrl(response.auth_url)
        ) {
          // Revert optimistic update; user needs to complete OAuth flow
          setConnections(previousConnections);
          if (popup) {
            if (!safeOAuthNavigate(popup, response.auth_url)) {
              popup.close();
            }
          }
          setActionInProgress(null);
          return;
        } else {
          // No OAuth needed — close the blank popup
          if (popup) popup.close();
        }
      }
    } catch (err) {
      setConnections(previousConnections);
      setError(
        isCurrentlyConnected
          ? t('mcp-disconnect-failed')
          : t('mcp-connect-failed'),
      );
    } finally {
      setActionInProgress(null);
    }
  };

  if (isLoading) {
    return (
      <div
        id="id-mcp-connections-loading-container"
        data-testid="mcp-connections-loading-container"
        className="px-4 py-3"
      >
        <div
          id="id-mcp-connections-loading-spinner-wrapper"
          data-testid="mcp-connections-loading-spinner-wrapper"
          className="flex items-center justify-center py-8"
        >
          <span
            id="id-mcp-connections-loading-spinner"
            data-testid="mcp-connections-loading-spinner"
            role="progressbar"
            aria-label="loading"
            className="inline-block size-6 animate-spin rounded-full border-2 border-brand-interactive border-t-transparent dark:border-brand-blue dark:border-t-transparent"
          />
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div
        id="id-mcp-connections-container"
        data-testid="mcp-connections-container"
        className="px-4 py-3"
      >
        <h2 className="text-base font-semibold leading-6 text-carbon-bodyText dark:text-carbon-bodyTextDark">
          {t('mcp-connections-title')}
        </h2>
        <span
          id="id-mcp-connections-description"
          data-testid="mcp-connections-description"
          className="mt-2 block text-xs font-normal leading-5 text-carbon-textSecondary dark:text-gray-400"
        >
          {t('mcp-connections-description')}
        </span>

        {error && (
          <div
            id="id-mcp-connections-error"
            data-testid="mcp-connections-error"
            className="mt-2 rounded bg-red-50 px-3 py-2 text-[13px] text-status-failRed dark:bg-red-950 dark:text-red-400"
          >
            {error}
          </div>
        )}

        {connections.length === 0 ? (
          <div
            id="id-mcp-connections-empty"
            data-testid="mcp-connections-empty"
            className="py-8 text-center text-sm text-carbon-textSecondary dark:text-gray-400"
          >
            {t('no-mcp-connections')}
          </div>
        ) : (
          <div
            id="id-mcp-connections-table-wrapper"
            data-testid="mcp-connections-table-wrapper"
            className="mt-4 overflow-x-auto"
          >
            <Table className="text-[13px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="font-semibold">{t('toggle')}</TableHead>
                  <TableHead className="font-semibold">{t('name')}</TableHead>
                  <TableHead className="font-semibold">
                    {t('credential-type')}
                  </TableHead>
                  <TableHead className="font-semibold">{t('domain')}</TableHead>
                  <TableHead className="font-semibold">
                    {t('connect')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.map((connection) => {
                  const isExternal = connection.credential_type !== 'internal';
                  const isConnected = !!connection.connected;
                  const isActionPending =
                    actionInProgress === connection.server_id;
                  const isLocked =
                    !isExternal &&
                    connection.user_enabled &&
                    LOCKED_OOTB_SERVER_NAMES.has(
                      connection.name.trim().toLowerCase(),
                    );

                  return (
                    <TableRow key={connection.server_id}>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Switch
                            checked={connection.user_enabled}
                            onCheckedChange={() => handleToggle(connection)}
                            disabled={isLocked || (isExternal && !isConnected)}
                            aria-label={
                              isLocked
                                ? t(
                                    'mcp-loop-server-locked',
                                    'Loop Genie relies on this MCP server — it cannot be disabled.',
                                  )
                                : `${t('toggle')} ${connection.name}`
                            }
                          />
                          {isLocked && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span
                                  className="text-gray-400 dark:text-gray-500"
                                  data-testid={`mcp-connections-locked-icon-${connection.server_id}`}
                                >
                                  <Locked size={14} />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                {t(
                                  'mcp-loop-server-locked',
                                  'Loop Genie relies on this MCP server — it cannot be disabled.',
                                )}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{formatLabel(connection.name)}</TableCell>
                      <TableCell>
                        {formatCredential(connection.credential_type)}
                      </TableCell>
                      <TableCell>
                        {(connection.domain || '').toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {isExternal && (
                          <button
                            className={[
                              'rounded border-none px-3.5 py-1 text-xs font-medium leading-[18px] text-white transition-colors duration-200',
                              isConnected
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-brand-interactive hover:bg-primary-hover dark:hover:bg-brand-blue',
                              isActionPending
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer',
                            ].join(' ')}
                            disabled={isActionPending}
                            onClick={() => handleConnectDisconnect(connection)}
                            aria-label={
                              isConnected
                                ? `${t('disconnect')} ${connection.name}`
                                : `${t('connect')} ${connection.name}`
                            }
                          >
                            {isActionPending
                              ? '...'
                              : isConnected
                                ? t('disconnect')
                                : t('connect')}
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default MCPConnectionsTab;
